import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { formatPhone, formatDuration, formatDateTime, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft,
  Phone,
  Envelope,
  Buildings,
  Calendar,
  Clock,
  Play,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  Warning
} from '@phosphor-icons/react';

const STATUS_OPTIONS = ['new', 'queued', 'calling', 'completed', 'failed', 'no_answer', 'callback_scheduled'];
const PRIORITY_OPTIONS = ['high', 'medium', 'low'];

function StatusBadge({ status }) {
  const styles = {
    new: 'badge-info',
    queued: 'badge-warning',
    calling: 'bg-yellow-500 text-black animate-pulse',
    completed: 'badge-success',
    failed: 'badge-error',
    no_answer: 'badge-neutral',
    callback_scheduled: 'badge-warning'
  };
  return (
    <span className={`badge ${styles[status] || 'badge-neutral'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

function OutcomeBadge({ outcome }) {
  const styles = {
    qualified: 'badge-success',
    closed: 'badge-success',
    not_qualified: 'badge-error',
    callback: 'badge-warning',
    voicemail: 'badge-neutral',
    hung_up: 'badge-error',
    transferred: 'badge-info'
  };
  return (
    <span className={`badge ${styles[outcome] || 'badge-neutral'}`}>
      {outcome ? outcome.replace('_', ' ') : 'pending'}
    </span>
  );
}

function SentimentIndicator({ sentiment }) {
  const colors = {
    positive: 'bg-emerald-500',
    neutral: 'bg-yellow-500',
    negative: 'bg-red-500'
  };
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${colors[sentiment] || 'bg-zinc-500'}`} />
      <span className="text-xs text-zinc-400 capitalize">{sentiment || 'unknown'}</span>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState('');
  const [callLoading, setCallLoading] = useState(false);

  useEffect(() => {
    loadLead();
    loadScripts();
  }, [id]);

  const loadLead = async () => {
    try {
      const response = await api.get(`/leads/${id}`);
      setLead(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/dashboard/leads');
    } finally {
      setLoading(false);
    }
  };

  const loadScripts = async () => {
    try {
      const response = await api.get('/scripts');
      setScripts(response.data.scripts);
    } catch (error) {
      // Silently fail
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/leads/${id}`, editData);
      toast.success('Lead updated');
      setEditing(false);
      loadLead();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      await api.delete(`/leads/${id}`);
      toast.success('Lead deleted');
      navigate('/dashboard/leads');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleCall = async () => {
    setCallLoading(true);
    try {
      const response = await api.post('/calls/initiate', {
        leadId: id,
        scriptId: selectedScript || undefined
      });
      toast.success('Call initiated');
      setShowCallModal(false);
      loadLead();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCallLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">Loading lead...</div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/leads"
            className="w-10 h-10 flex items-center justify-center rounded-sm bg-zinc-800 hover:bg-zinc-700 transition-colors"
            data-testid="back-to-leads"
          >
            <ArrowLeft size={20} className="text-zinc-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-medium text-zinc-50 tracking-tight">
              {lead.firstName} {lead.lastName}
            </h1>
            <p className="text-zinc-400 font-mono">{formatPhone(lead.phone)}</p>
          </div>
          <StatusBadge status={lead.status} />
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setEditing(!editing)}
            className="btn-secondary flex items-center gap-2"
            data-testid="edit-lead-btn"
          >
            <PencilSimple size={18} /> {editing ? 'Cancel' : 'Edit'}
          </button>
          <button
            onClick={() => setShowCallModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="call-now-btn"
          >
            <Phone size={18} weight="fill" /> Call Now
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h3 className="text-lg font-medium text-zinc-50 mb-4">Contact Information</h3>
            {editing ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">First Name</Label>
                  <Input
                    value={editData.firstName || ''}
                    onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Last Name</Label>
                  <Input
                    value={editData.lastName || ''}
                    onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Phone</Label>
                  <Input
                    value={editData.phone || ''}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Email</Label>
                  <Input
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Company</Label>
                  <Input
                    value={editData.company || ''}
                    onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Priority</Label>
                  <Select value={editData.priority} onValueChange={(v) => setEditData({ ...editData, priority: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400">Status</Label>
                  <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-zinc-400">Notes</Label>
                  <Textarea
                    value={editData.notes || ''}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    rows={3}
                  />
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditing(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-500">
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-y-4">
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">Phone</div>
                    <div className="text-zinc-50 font-mono">{formatPhone(lead.phone)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Envelope size={18} className="text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">Email</div>
                    <div className="text-zinc-50">{lead.email || '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Buildings size={18} className="text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">Company</div>
                    <div className="text-zinc-50">{lead.company || '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">Created</div>
                    <div className="text-zinc-50">{formatDateTime(lead.createdAt)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Call History */}
          <div className="card">
            <h3 className="text-lg font-medium text-zinc-50 mb-4">Call History</h3>
            {lead.callHistory?.length > 0 ? (
              <div className="space-y-4">
                {lead.callHistory.map((call) => (
                  <div key={call.id} className="bg-zinc-900 rounded-sm p-4 border border-zinc-800" data-testid={`call-${call.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <OutcomeBadge outcome={call.outcome} />
                        <SentimentIndicator sentiment={call.sentiment} />
                      </div>
                      <div className="text-sm text-zinc-500">
                        {formatDateTime(call.createdAt)}
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-zinc-500">Duration:</span>{' '}
                        <span className="text-zinc-300 font-mono">{formatDuration(call.durationSeconds)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Status:</span>{' '}
                        <span className="text-zinc-300 capitalize">{call.status}</span>
                      </div>
                    </div>
                    {call.transcriptSummary && (
                      <div className="mt-3 pt-3 border-t border-zinc-800">
                        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Summary</div>
                        <p className="text-sm text-zinc-300">{call.transcriptSummary}</p>
                      </div>
                    )}
                    {call.recordingUrl && (
                      <div className="mt-3">
                        <a
                          href={call.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-400 hover:text-blue-300"
                        >
                          Listen to Recording
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No calls yet</p>
            )}
          </div>

          {/* Notes */}
          {!editing && lead.notes && (
            <div className="card">
              <h3 className="text-lg font-medium text-zinc-50 mb-4">Notes</h3>
              <p className="text-zinc-300 whitespace-pre-wrap">{lead.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h3 className="text-lg font-medium text-zinc-50 mb-4">Lead Stats</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Total Calls</span>
                <span className="text-zinc-50 font-mono">{lead.callHistory?.length || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Score</span>
                <span className="text-zinc-50 font-mono">{lead.score || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Source</span>
                <span className="text-zinc-50 capitalize">{lead.source?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Priority</span>
                <span className={`badge ${lead.priority === 'high' ? 'badge-error' : lead.priority === 'medium' ? 'badge-warning' : 'badge-neutral'}`}>
                  {lead.priority}
                </span>
              </div>
            </div>
          </div>

          {/* Extracted Data */}
          {lead.callHistory?.some(c => c.extractedData && Object.keys(c.extractedData).length > 0) && (
            <div className="card">
              <h3 className="text-lg font-medium text-zinc-50 mb-4">Extracted Data</h3>
              <div className="space-y-2">
                {lead.callHistory
                  .filter(c => c.extractedData)
                  .flatMap(c => Object.entries(c.extractedData || {}))
                  .map(([key, value], i) => (
                    <div key={i} className="flex justify-between items-center text-sm">
                      <span className="text-zinc-400 capitalize">{key.replace('_', ' ')}</span>
                      <span className="text-zinc-50">{String(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Custom Fields */}
          {lead.customFields && Object.keys(lead.customFields).length > 0 && (
            <div className="card">
              <h3 className="text-lg font-medium text-zinc-50 mb-4">Custom Fields</h3>
              <div className="space-y-2">
                {Object.entries(lead.customFields).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center text-sm">
                    <span className="text-zinc-400 capitalize">{key.replace('_', ' ')}</span>
                    <span className="text-zinc-50">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="card">
            <h3 className="text-lg font-medium text-zinc-50 mb-4">Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => setShowCallModal(true)}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="sidebar-call-btn"
              >
                <Phone size={18} weight="fill" /> Call Now
              </button>
              <button
                onClick={handleDelete}
                className="btn-secondary w-full flex items-center justify-center gap-2 text-red-400 hover:text-red-300"
                data-testid="delete-lead-btn"
              >
                <Trash size={18} /> Delete Lead
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Call Modal */}
      <Dialog open={showCallModal} onOpenChange={setShowCallModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="call-modal">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Initiate Call</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-zinc-400">
              Call <span className="text-zinc-50 font-medium">{lead.firstName} {lead.lastName}</span> at{' '}
              <span className="text-zinc-50 font-mono">{formatPhone(lead.phone)}</span>
            </p>
            <div>
              <Label className="text-zinc-400">Call Script (optional)</Label>
              <Select value={selectedScript || "none"} onValueChange={(v) => setSelectedScript(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="script-select">
                  <SelectValue placeholder="Select a script" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No script</SelectItem>
                  {scripts.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleCall}
              disabled={callLoading}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="confirm-call-btn"
            >
              {callLoading ? 'Initiating...' : 'Start Call'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
